import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { ThrottlingGroup } from '../../../@types/throttlingGroup';
import { Button } from '@nps.today/component-library';
import { CampaignList } from 'pages/campaigns/components/CampaignList';
import { useEffect, useRef, useState } from 'react';
import { useCustomDispatchWithoutPromise } from 'redux/dispatch';
import { DataGridKey, setColumnVisibility, setFiltering } from 'redux/slices/datagrid';
import { RootState, useSelector } from 'redux/store';
import { useTranslator } from 'translation/useTranslator';

const getInitialFilterModel = (throttlingGroupId?: string) => {
  return {
    items: [
      {
        field: 'throttlingGroupId',
        operator: 'is',
        value: throttlingGroupId || 'default'
      }
    ]
  };
};

type ThrottlingGroupCampaignsDialogProps = {
  dataGridKey: DataGridKey;
  throttlingGroup?: ThrottlingGroup;
  resetThrottlingGroup: () => void;
};

const ThrottlingGroupCampaignsDialog = ({
  dataGridKey,
  throttlingGroup,
  resetThrottlingGroup
}: ThrottlingGroupCampaignsDialogProps) => {
  const translator = useTranslator();
  const customDispatchWithoutPromise = useCustomDispatchWithoutPromise();
  const { filtering } = useSelector((state: RootState) => state.datagrid);
  const [selectionModel, setSelectionModel] = useState<number[]>([]);

  const intitialColumnsVisibilityModelRef = useRef({
    id: false,
    logo: false,
    npsScore: false,
    respondentCount: false,
    responseCount: false,
    responsePercent: false,
    created: false,
    reminder: false,
    sendoutDelay: false,
    forceAnonymous: false,
    allowDuplicates: false,
    content: false,
    type: false,
    throttlingDays: true,
    throttlingGroupId: true,
    active: true
  });

  useEffect(() => {
    if (!throttlingGroup) return;

    customDispatchWithoutPromise({
      action: setFiltering,
      actionParameters: [dataGridKey, getInitialFilterModel(throttlingGroup.id)],
      disableSuccessMessage: true
    });
    customDispatchWithoutPromise({
      action: setColumnVisibility,
      actionParameters: [dataGridKey, intitialColumnsVisibilityModelRef.current],
      disableSuccessMessage: true
    });
  }, [dataGridKey, customDispatchWithoutPromise, throttlingGroup]);

  const onClose = () => {
    resetThrottlingGroup();
  };

  const currentFirstFilterValue = (filtering[dataGridKey]?.items?.[0]?.value || '') as string;
  const isOnlyShowingCampaignFromGroup =
    filtering[dataGridKey]?.items?.length === 1 &&
    (currentFirstFilterValue.toLocaleLowerCase() === throttlingGroup?.id?.toLocaleLowerCase() ||
      currentFirstFilterValue.toLocaleLowerCase() === throttlingGroup?.name?.toLocaleLowerCase()) &&
    filtering[dataGridKey]?.items?.[0]?.operator === 'is';

  return (
    <Dialog open={Boolean(throttlingGroup?.name)} onClose={onClose} maxWidth="xl">
      <DialogTitle style={{ marginBottom: '1rem' }}>
        {isOnlyShowingCampaignFromGroup
          ? translator.campaignsInThrottlingGroup(throttlingGroup?.name ?? '')
          : translator.campaigns()}
      </DialogTitle>
      <DialogContent style={{ width: '80vw' }}>
        <CampaignList
          selectionModel={selectionModel}
          setSelectionModel={setSelectionModel}
          dataGridKey={dataGridKey}
          height="70vh"
          intitialColumnsHidden={intitialColumnsVisibilityModelRef.current}
        />
      </DialogContent>
      <DialogActions>
        <Button
          dataTestId="throttling-groups-campaign-dialog-clsoe"
          color="primary"
          onClick={onClose}
        >
          {translator.close()}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ThrottlingGroupCampaignsDialog;
